import React, { memo, useCallback, useState } from 'react';

import { Divider, Input, Text, useTheme } from '@desygner/ui-common-components';
import { Unit } from '@desygner/ui-common-svg-utils';
import { useTranslation } from '@desygner/ui-common-translation';

import {
  StyledGridPreviewContainer,
  StyledGuidesContainer,
  StyledGuidesContentContainer,
  StyledInputContainer,
  StyledTitleContainer,
} from './Guides.style';
import { GridPreview } from './components/GridPreview';

export interface GuidesSettings {
  row: number;
  column: number;
  gapX: number;
  gapY: number;
  marginX: number;
  marginY: number;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: string;
}

export interface MinimumGuideValues {
  minimumGap: number;
  minimumMargin: number;
  minimumRow: number;
  minimumColumn: number;
}

export interface GuidesProps {
  settings: GuidesSettings;
  defaultSettings?: GuidesSettings[];
  onChangeGuides: (settings: GuidesSettings) => void;
  minimumGuideValues: MinimumGuideValues;
  unit?: Unit;
}

export interface GridItemProps {
  row: number;
  column: number;
  selected?: boolean;
  defaultSettings: GuidesSettings;
  setSettings: React.Dispatch<React.SetStateAction<GuidesSettings>>;
  onChangeGuides: (settings: GuidesSettings) => void;
}

export const Guides = memo(function Guides({
  defaultSettings,
  settings: initialSettings,
  onChangeGuides,
  minimumGuideValues,
  unit = 'px',
}: GuidesProps): React.ReactElement {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(initialSettings);
  const theme = useTheme();

  const onChangeRow = useCallback(
    (value: string) => {
      let newRow = 1;

      try {
        newRow = Math.max(parseInt(value), 1);
      } catch {
        newRow = 1;
      }

      setSettings((prev) => {
        const newSettings = { ...prev, row: newRow };

        onChangeGuides(newSettings);
        return newSettings;
      });
    },
    [onChangeGuides]
  );

  const onChangeColumn = useCallback(
    (value: string) => {
      let newColumn = 1;

      try {
        newColumn = Math.max(parseInt(value), 1);
      } catch {
        newColumn = 1;
      }

      setSettings((prev) => {
        const newSettings = { ...prev, column: newColumn };

        onChangeGuides(newSettings);
        return newSettings;
      });
    },
    [onChangeGuides]
  );

  const onChangeGapX = useCallback(
    (value: string) => {
      let newGapX = 0;

      try {
        newGapX = Math.max(parseInt(value), 0);
      } catch {
        newGapX = 0;
      }

      setSettings((prev) => {
        const newSettings = { ...prev, gapX: newGapX };

        onChangeGuides({ ...newSettings });
        return newSettings;
      });
    },
    [onChangeGuides]
  );

  const onChangeGapY = useCallback(
    (value: string) => {
      let newGapY = 0;

      try {
        newGapY = Math.max(parseInt(value), 0);
      } catch {
        newGapY = 0;
      }

      setSettings((prev) => {
        const newSettings = { ...prev, gapY: newGapY };

        onChangeGuides({ ...newSettings });
        return newSettings;
      });
    },
    [onChangeGuides]
  );

  const onChangeMarginX = useCallback(
    (value: string) => {
      let newMarginX = 0;

      try {
        newMarginX = Math.max(parseInt(value), 0);
      } catch {
        newMarginX = 0;
      }

      setSettings((prev) => {
        const newSettings = {
          ...prev,
          marginX: newMarginX,
        };

        onChangeGuides({
          ...newSettings,
        });

        return newSettings;
      });
    },
    [onChangeGuides]
  );

  const onChangeMarginY = useCallback(
    (value: string) => {
      let newMarginY = 0;

      try {
        newMarginY = Math.max(parseInt(value), 0);
      } catch {
        newMarginY = 0;
      }

      setSettings((prev) => {
        const newSettings = {
          ...prev,
          marginY: newMarginY,
        };

        onChangeGuides({
          ...newSettings,
        });

        return newSettings;
      });
    },
    [onChangeGuides]
  );

  return (
    <StyledGuidesContainer>
      <StyledGuidesContentContainer>
        <StyledGridPreviewContainer>
          {defaultSettings?.map((defaultSetting, index) => (
            <GridItem
              key={index}
              row={defaultSetting.row}
              column={defaultSetting.column}
              defaultSettings={defaultSetting}
              selected={
                settings.row === defaultSetting.row && settings.column === defaultSetting.column
              }
              setSettings={setSettings}
              onChangeGuides={onChangeGuides}
            />
          ))}
        </StyledGridPreviewContainer>

        <StyledGridPreviewContainer>
          <StyledInputContainer>
            <Input
              id="guides-columns-input"
              name="column"
              type="number"
              value={settings.column}
              label={t('general.columns')}
              onChange={onChangeColumn}
              min={minimumGuideValues.minimumColumn}
            />
          </StyledInputContainer>
          <StyledInputContainer>
            <StyledTitleContainer>
              <Text size="Body">{t('general.gap')}</Text>
              <Text size="Small" color={theme.Color.Neutrals.Gray}>
                {unit}
              </Text>
            </StyledTitleContainer>
            <Input
              id="guides-columns-gap-input"
              name="gapX"
              type="number"
              value={settings.gapX}
              onChange={onChangeGapX}
              min={minimumGuideValues.minimumGap}
            />
          </StyledInputContainer>
          <StyledInputContainer>
            <StyledTitleContainer>
              <Text size="Body">{t('general.margin')}</Text>
              <Text size="Small" color={theme.Color.Neutrals.Gray}>
                {unit}
              </Text>
            </StyledTitleContainer>
            <Input
              id="guides-columns-margin-input"
              name="marginX"
              type="number"
              value={settings.marginX}
              onChange={onChangeMarginX}
              min={minimumGuideValues.minimumMargin}
            />
          </StyledInputContainer>
        </StyledGridPreviewContainer>

        <Divider />

        <StyledGridPreviewContainer>
          <StyledInputContainer>
            <Input
              id="guides-columns-input"
              name="row"
              type="number"
              value={settings.row}
              label={t('general.rows')}
              onChange={onChangeRow}
              min={minimumGuideValues.minimumRow}
            />
          </StyledInputContainer>

          <StyledInputContainer>
            <StyledTitleContainer>
              <Text size="Body">{t('general.gap')}</Text>
              <Text size="Small" color={theme.Color.Neutrals.Gray}>
                {unit}
              </Text>
            </StyledTitleContainer>
            <Input
              id="guides-columns-gap-input"
              name="gapY"
              type="number"
              value={settings.gapY}
              onChange={onChangeGapY}
              min={minimumGuideValues.minimumGap}
            />
          </StyledInputContainer>

          <StyledInputContainer>
            <StyledTitleContainer>
              <Text size="Body">{t('general.margin')}</Text>
              <Text size="Small" color={theme.Color.Neutrals.Gray}>
                {unit}
              </Text>
            </StyledTitleContainer>
            <Input
              id="guides-columns-margin-input"
              name="marginY"
              type="number"
              value={settings.marginY}
              onChange={onChangeMarginY}
              min={minimumGuideValues.minimumMargin}
            />
          </StyledInputContainer>
        </StyledGridPreviewContainer>
      </StyledGuidesContentContainer>
    </StyledGuidesContainer>
  );
});

const GridItem = memo(function GridItem({
  row,
  column,
  selected,
  defaultSettings,
  setSettings,
  onChangeGuides,
}: GridItemProps): React.ReactElement {
  const handleClick = useCallback(() => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        row: defaultSettings.row,
        column: defaultSettings.column,
      };

      onChangeGuides(newSettings);
      return newSettings;
    });
  }, [setSettings, onChangeGuides, defaultSettings.row, defaultSettings.column]);

  return <GridPreview row={row} column={column} onClick={handleClick} selected={selected} />;
});
